import * as React from 'react';
import { Column } from '@tanstack/react-table';
import { Check, PlusCircle } from 'lucide-react';

import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Separator } from 'components//ui/separator';
import { useTranslation } from 'react-i18next';

/**
 * DataTableFacetedFilter Component
 *
 * A filter component that allows users to filter data based on predefined options.
 * The component displays a button with an icon, a label, and the number of selected filters.
 * It allows users to open a popover to select from a list of available options.
 * It integrates with `@tanstack/react-table` for setting the filter values based on the selected options.
 *
 * Features:
 * - Displays a button with the title and selected options.
 * - Allows users to filter data based on options passed as props.
 * - Displays a popover with a list of options for users to select.
 * - Shows a badge displaying the number of selected filters.
 * - Allows clearing the applied filters.
 *
 * @template TData - The type of data used in the table.
 * @template TValue - The type of value for the column being filtered.
 *
 * @param {Column<TData, TValue>} [column] - The column to be filtered, passed from `@tanstack/react-table`.
 * @param {string} [title] - The title to be displayed for the filter button.
 * @param {{ label: string; value: string; icon?: React.ComponentType<{ className?: string }> }[]} options - A list of options to be used in the filter. Each option contains a label, a value, and an optional icon.
 *
 * @returns {JSX.Element} A faceted filter component.
 */

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  triggerClassName?: string;
  /** Controlled selection, for tables that keep filters outside a tanstack column. */
  value?: string[];
  onValueChange?: (values: string[]) => void;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  triggerClassName,
  value,
  onValueChange,
  options,
}: Readonly<DataTableFacetedFilterProps<TData, TValue>>) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(value ?? (column?.getFilterValue() as string[]));
  const { t } = useTranslation();

  const setSelected = (values: string[]) => {
    if (onValueChange) {
      onValueChange(values);
      return;
    }
    column?.setFilterValue(values.length ? values : undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-10 border border-primary text-primary-600 rounded-3xl',
            triggerClassName
          )}
        >
          <PlusCircle />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="outline" className="rounded-md px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="outline" className="rounded-md px-1 font-normal">
                    {selectedValues.size} {t('SELECTED', 'selected')}
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="outline"
                        key={option.value}
                        className="rounded-md px-1 font-normal"
                      >
                        {t(option.label)}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      setSelected(Array.from(selectedValues));
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'border-transparent bg-foreground text-background [&_svg]:text-background'
                          : 'border-muted-foreground/50 opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    <span>{t(option.label)}</span>
                    {facets?.get(option.value) && (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => setSelected([])}
                    className="justify-center text-center"
                  >
                    {t('CLEAR_FILTERS')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
